// @ts-nocheck

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../../client';
import ArrowButton from '../../../components/ArrowButton';
import SwipePagination from '../../../components/SwipePagination';
import WinaLoader from '../../../components/WinaLoader';
import { usePrevious } from '../../../hooks';
import theme from '../../../styled-components/dark';
import { ZIndices } from '../../../styled-components/dark/common';
import { KLRoundViewByOrder } from '../constants/order';
import KLEliminatedViewContainer from '../containers/KLEliminatedViewContainer';
import KLRoundContainer from '../containers/KLRoundContainer';
import { KLGlobalStatuses, type KLGlobalStatus } from '../types/global';

import KLSwipe from './common/KLSwipe';
import KLViewWrapper from './common/KLViewWrapper';

const StyledKLViewWrapper = styled(KLViewWrapper)`
  padding: 0;
`;

const StyledWinaLoader = styled(WinaLoader)`
  ${({ $isLoading }) => css`
    position: absolute;
    top: 0;
    left: 0;
    margin: 0;
    width: 100%;
    height: 100%;
    opacity: ${$isLoading ? 1 : 0};
  `}
`;

const PaginationWrapper = styled.div`
  position: absolute;
  z-index: ${ZIndices.overlay + 1};
  top: 50%;
  left: 0;

  width: 100%;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;

  font-size: 80%;
`;

const PaginationBtnSharedStyle = css`
  height: 3.5em;
  width: 2.5em;

  opacity: 0.7;
  border-radius: 0.3em;

  pointer-events: auto;

  ${({ disabled }) => {
    if (disabled) {
      return css`
        opacity: 0;
        pointer-events: none;
      `;
    }
  }}

  &:not(::disabled):hover {
    opacity: 1;
  }
`;

const PrevButton = styled(ArrowButton)`
  ${PaginationBtnSharedStyle}
  margin-left: 0.3em;
`;
const NextButton = styled(ArrowButton)`
  ${PaginationBtnSharedStyle}
  margin-right: 0.3em;
`;

const OpeEndingTag = styled.div`
  position: absolute;
  top: 93.7%;
  left: 50%;
  width: 90%;
  transform: translate(-50%, -90%);
  pointer-events: none;
  opacity: 0.8;
  background: ${theme.KLHome.opeEndingTagBackgroundColor};
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.3em 0.5em;
  border-radius: 0.3em;
  font-weight: 600;
  font-size: 0.9em;
`;

const StyledSwipePagination = styled(SwipePagination)`
  position: absolute;
  z-index: 1;
  bottom: 1em;
  left: 50%;
  transform: translateX(-50%);
`;

type KLHomeProps = {
  userRoundIndex: number;
  index?: number | null;
  userRoundIndex?: number;
  setIndex: (index: number) => void;
  isUserEliminated: boolean;
  navigationIsAllowed: boolean;
  globalStatus: KLGlobalStatus;
  className?: string;
};

const KLHome = ({
  index,
  userRoundIndex,
  setIndex,
  isUserEliminated,
  navigationIsAllowed,
  globalStatus,
  className = '',
}: KLHomeProps) => {
  const [swipeIsLoaded, setSwipeIsLoaded] = useState(false);
  const prevUserRoundIndex = usePrevious(userRoundIndex);
  const prevUserRoundIndexRef = useRef(prevUserRoundIndex);
  useEffect(() => {
    prevUserRoundIndexRef.current = prevUserRoundIndex;
  }, [prevUserRoundIndex]);
  const setIndexRef = useRef();

  useEffect(() => {
    setIndexRef.current = setIndex;
  }, [setIndex]);

  const content = useMemo(() => {
    const tmp = KLRoundViewByOrder.map((roundId) => {
      return (
        <KLRoundContainer key={`${roundId}-view`} roundId={roundId}>
          {globalStatus === KLGlobalStatuses.ENDING ? (
            <OpeEndingTag>Koh-Lanta, c&apos;est terminé pour cette saison !</OpeEndingTag>
          ) : null}
        </KLRoundContainer>
      );
    });

    if (isUserEliminated) {
      tmp.push(<KLEliminatedViewContainer key="eliminated-view" />);
    }

    return tmp;
  }, [globalStatus, isUserEliminated]);

  const onPrevClick = useCallback(
    (e) => {
      e.stopPropagation();

      if (setIndexRef.current && index > 0) {
        setIndexRef.current(index - 1);
      }
    },
    [index],
  );

  const onNextClick = useCallback(
    (e) => {
      e.stopPropagation();

      if (setIndexRef.current && index < content?.length) {
        setIndexRef.current(index + 1);
      }
    },
    [content, index],
  );

  const isReady = useMemo(() => !!(index !== null && content?.length), [content, index]);
  const shouldDisplayPagination = useMemo(
    () => swipeIsLoaded && isReady && navigationIsAllowed,
    [swipeIsLoaded, isReady, navigationIsAllowed],
  );

  useEffect(() => {
    if (typeof setIndexRef.current === 'function' && typeof prevUserRoundIndexRef.current === 'number') {
      if (isUserEliminated) {
        setIndexRef.current(KLRoundViewByOrder.length);
      } else {
        setIndexRef.current(userRoundIndex);
      }
    }
  }, [userRoundIndex, isUserEliminated]);

  return isReady ? (
    <StyledKLViewWrapper className={className}>
      {shouldDisplayPagination && !Target.__MOBILE__ ? (
        <PaginationWrapper>
          <PrevButton angle={180} onClick={onPrevClick} disabled={index <= 0} />
          <NextButton angle={0} onClick={onNextClick} disabled={index >= content.length - 1} />
        </PaginationWrapper>
      ) : null}
      <StyledWinaLoader $isLoading={!swipeIsLoaded} />
      <KLSwipe
        setIndex={setIndexRef.current}
        indexToGo={index}
        fillSpace
        showNextPage
        noScale
        useKeyboard={false}
        noPadding
        disable={!Target.__MOBILE__ || !navigationIsAllowed}
        setIsLoaded={setSwipeIsLoaded}
        delayShowing={0}
        controlled
        alwaysRenderEvery
      >
        {content}
      </KLSwipe>

      {shouldDisplayPagination ? (
        <StyledSwipePagination index={index} maxViews={KLRoundViewByOrder.length + (isUserEliminated ? 1 : 0)} />
      ) : null}
    </StyledKLViewWrapper>
  ) : null;
};

export default KLHome;
